import React from 'react';

interface ResponsiveOptimizedImageProps {
  src: string;
  alt: string;
  className?: string;
  width?: number | string;
  height?: number | string;
  loading?: 'lazy' | 'eager';
  sizes?: number[];
  sizesAttribute?: string;
}

const ResponsiveOptimizedImage: React.FC<ResponsiveOptimizedImageProps> = ({
  src,
  alt,
  className = '',
  width,
  height,
  loading = 'lazy',
  sizes = [640, 960, 1280],
  sizesAttribute = '(max-width: 640px) 640px, (max-width: 960px) 960px, 1280px',
}) => {
  // Function to get WebP URL from original URL
  const getWebpUrl = (originalUrl: string): string => {
    try {
      // If the URL is already a WebP, return it as is
      if (originalUrl.endsWith('.webp')) {
        return originalUrl;
      }
      
      // For Firebase Storage URLs, we need to handle the URL parameters
      if (originalUrl.includes('firebasestorage.googleapis.com')) {
        const url = new URL(originalUrl);
        const pathParts = url.pathname.split('/o/');
        if (pathParts.length !== 2) {
          console.warn('Invalid Firebase Storage URL format');
          return originalUrl;
        }

        // Get the file path and create WebP path by just replacing the extension
        const filePath = decodeURIComponent(pathParts[1].split('?')[0]);
        const webpPath = filePath.replace(/\.[^/.]+$/, '.webp');

        // Reconstruct the URL with the correct Firebase Storage domain and bucket
        return `${url.protocol}//firebasestorage.googleapis.com/v0/b/teluaseghapp.firebasestorage.app/o/${encodeURIComponent(webpPath)}${url.search}`;
      }
      
      // For other URLs, simply replace the extension
      return originalUrl.replace(/\.[^/.]+$/, '.webp');
    } catch (error) {
      console.error('Error converting URL to WebP:', error);
      return originalUrl; // Return original URL if there's an error
    }
  };

  const webpSrc = getWebpUrl(src);

  return (
    <picture>
      <source
        srcSet={webpSrc}
        type="image/webp"
        sizes={sizesAttribute}
      />
      <img
        src={src}
        alt={alt}
        className={className}
        width={width}
        height={height}
        loading={loading}
        sizes={sizesAttribute}
      />
    </picture>
  );
};

export default ResponsiveOptimizedImage; 